import { Form } from 'antd';
import Text from 'components/Text/Text';
import {
	IWithAdditionalValidationContext,
	WithAdditionalValidationContext,
} from 'components/WithAdditionalValidation/withAdditionalValidationContext';
import { IRestaurantBankSettingsForUpdate } from 'types';
import { NEW_BANK_SETTINGS_BLOCK_PREFIX } from 'appConstants';
import BankSettingsBlock from './components/BankSettingsBlock/BankSettingsBlock';
import BtnBlock from './components/BtnBlock';
import { BankSettingsContext, IBankSettingsContext } from '../bankSettingsState/bankSettingsContext';
import {
	EditRestaurantContext,
	IEditRestaurantContext,
} from 'modules/CustomersAndRestaurants/Restaurants/EditRestaurant/editRestaurantState/editRestaurantContext';
import style from './InputFields.module.scss';
import React, { useContext, useEffect } from 'react';
import { preventDefaultForEnterKey } from 'utils/helpers';

const InputFields = () => {
	const { setMainFormValidationFail, additionalValidationErrors } = useContext<IWithAdditionalValidationContext>(
		WithAdditionalValidationContext,
	);
	const { restaurantId } = useContext<IEditRestaurantContext>(EditRestaurantContext);
	const { changeTypeOfModalWindow, setDataForSubmit, bankSettings, initialFormData } =
		useContext<IBankSettingsContext>(BankSettingsContext);
	const [form] = Form.useForm();

	useEffect(() => {
		bankSettings.forEach((el) => {
			form.setFieldsValue({
				[`priority-${el.id}`]: el.priority,
				[`merchantId-${el.id}`]: el.merchantId,
				[`merchantName-${el.id}`]: el.merchantName,
				[`paymentTypeId-${el.id}`]: el.paymentTypeId,
				[`type-${el.id}`]: el.acquirer?.id,
				[`sbpAccountNumber-${el.id}`]: el.sbpAccount,
				[`sbpLogin-${el.id}`]: el.sbpUserLogin,
				[`yandexPaymentType-${el.id}`]: el.yandexPaymentTypes,
				[`yandexApiKey-${el.id}`]: el.yandexApiKey,
				[`vivaApiKey-${el.id}`]: el.vivaApiKey,
				[`vivaApiSecret-${el.id}`]: el.vivaApiSecret,
				[`yandexChequeApiToken-${el.id}`]: el.yandexChequeApiToken,
				[`yandexChequeApiPartnerId-${el.id}`]: el.yandexChequeApiPartnerId,
				[`accNumber-${el.id}`]: el.accNumber,
				[`sector-${el.id}`]: el.sector !== null ? String(el.sector) : '',
				[`sdRef-${el.id}`]: el.sdRef,
				[`isTipsSupported-${el.id}`]: el.isTipsSupported,
				[`merchantAccount-${el.id}`]: el.adyen?.merchantAccount || null,
				[`store-${el.id}`]: el.adyen?.store || null,
				[`apiKey-${el.id}`]: el.adyen?.apiKey || null,
				[`checkoutUrlPrefix-${el.id}`]: el.adyen?.checkoutUrlPrefix || null,
				[`merchantBalanceAccountId-${el.id}`]: el.adyen?.merchantBalanceAccountId || null,
				[`kmBalanceAccountId-${el.id}`]: el.adyen?.kmBalanceAccountId || null,
				[`smsCode-${el.id}`]: el?.smsCode || null,
				[`tBankPlaceId-${el.id}`]: el.tBankPlaceId || null,
				[`masterWallet-${el.id}`]: el.masterWallet || null,
			});
		});
	}, [bankSettings]);

	const submit = async () => {
		setMainFormValidationFail(false);

		const finalBankSettings: IRestaurantBankSettingsForUpdate[] = bankSettings.map((el, i) => {
			const isNewBankSettingsBlock =
				el.id.slice(0, NEW_BANK_SETTINGS_BLOCK_PREFIX.length) === NEW_BANK_SETTINGS_BLOCK_PREFIX;

			return {
				id: isNewBankSettingsBlock ? null : el.id,
				priority: Number(el.priority),
				yandexApiKey: el.yandexApiKey || null,
				yandexPaymentTypes: el.yandexPaymentTypes?.length ? el.yandexPaymentTypes : null,
				vivaApiKey: el.vivaApiKey || null,
				vivaApiSecret: el.vivaApiSecret || null,
				isVivaSecretUpdated:
					initialFormData?.bankSettings[i]?.vivaApiKey && initialFormData?.bankSettings[i]?.vivaApiSecret
						? el.vivaApiSecret !== initialFormData.bankSettings[i].vivaApiSecret
						: true,
				yandexChequeApiToken: el.yandexChequeApiToken,
				yandexChequeApiPartnerId: el.yandexChequeApiPartnerId,
				isYandexChequeSecretUpdated:
					initialFormData?.bankSettings[i]?.yandexChequeApiToken &&
					initialFormData?.bankSettings[i]?.yandexChequeApiPartnerId
						? el.yandexChequeApiToken !== initialFormData.bankSettings[i].yandexChequeApiToken
						: true,
				accNumber: el.accNumber || null,
				sector: el.sector ? Number(el.sector) : null,
				sdRef: el.sdRef || null,
				sbpAccount: el.sbpAccount || null,
				sbpUserLogin: el.sbpUserLogin || null,
				acquirerId: el.acquirer?.id || '',
				logoPath: el.logoPath,
				merchantId: el.merchantId,
				merchantName: el.merchantName,
				paymentTemplateFilePath: el.paymentTemplateFilePath,
				isTipsSupported: el.isTipsSupported,
				isFeeFiscalized: el.isFeeFiscalized,
				generateQrInstantly: el.generateQrInstantly,
				paymentTypeId: el.paymentTypeId || '',
				smsCode: el.smsCode || null,
				tBankPlaceId: el.tBankPlaceId || null,
				masterWallet: el.masterWallet || null,
				adyen: el.adyen
					? {
							merchantAccount: el.adyen.merchantAccount,
							store: el.adyen.store,
							apiKey:
								initialFormData?.bankSettings[i]?.adyen?.apiKey === el.adyen.apiKey
									? null
									: el.adyen.apiKey,
							checkoutUrlPrefix: el.adyen.checkoutUrlPrefix,
							splitPayments3S: el.adyen.splitPayments3S,
							merchantBalanceAccountId: el.adyen.splitPayments3S
								? el.adyen.merchantBalanceAccountId
								: null,
							kmBalanceAccountId: el.adyen.splitPayments3S ? el.adyen.kmBalanceAccountId : null,
					  }
					: null,
			};
		});

		setDataForSubmit({
			restaurantId,
			restaurantBankSettings: finalBankSettings,
		});
		additionalValidationErrors.length
			? setMainFormValidationFail(true)
			: changeTypeOfModalWindow('toConfirmSubmit');
	};

	const handleFormValidationFail = () => {
		setMainFormValidationFail(true);
	};

	return (
		<div data-test="InputFields">
			<Form
				form={form}
				name="basic"
				onFinish={submit}
				onFinishFailed={handleFormValidationFail}
				onKeyPress={preventDefaultForEnterKey}
				layout="vertical">
				<div className={style.fieldsWrapper}>
					<Text
						translationNameSpace="form"
						translationKey="bankSettingsHeading"
						theme="secondary"
						size="l"
						weight="bold"
					/>
					<BankSettingsBlock />
				</div>
				<BtnBlock />
			</Form>
		</div>
	);
};

export default InputFields;
